import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import slugify from 'slugify';
import React from 'react';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function cnClient(...inputs: ClassValue[]) {
	return clsx(inputs);
}

export function newsUrl(title: string, id: number) {
	return `/haber/${slugify(title, { lower: true, strict: true })}-${id}`;
}

export function calculateReadingTime(content: any) {
	if (!content?.blocks?.length) return { minutes: 0, seconds: 0, text: '0 dk' };

	const textBlocks = content.blocks.filter((block: any) => ['paragraph', 'header'].includes(block.type));

	let totalWords = 0;
	for (const block of textBlocks) {
		const plainText = block.data.text
			.replace(/<[^>]+>/g, '')
			.replace(/&nbsp;/g, ' ')
			.trim();

		const words = plainText.split(/\s+/).filter(Boolean);
		totalWords += words.length;
	}

	const wordsPerMinute = 180;
	const totalMinutes = totalWords / wordsPerMinute;

	const minutes = Math.floor(totalMinutes);
	const seconds = Math.round((totalMinutes - minutes) * 60);

	return {
		minutes,
		seconds,
		text: minutes < 1 ? `${seconds} sn` : seconds > 20 ? `${minutes + 1} dk` : `${minutes} dk`,
		totalWords,
	};
}

export function getSourceIcon(name: string) {
	switch (name.toLowerCase()) {
		case 'ajansspor':
			return '/icons/ajansspor.svg';
		case 'twitter':
			return '/icons/social/x.svg';
		case 'facebook':
			return '/icons/social/facebook.svg';
		case 'instagram':
			return '/icons/social/instagram.svg';
		case 'youtube':
			return '/icons/social/youtube.svg';
		case 'tiktok':
			return '/icons/social/tiktok.svg';
		default:
			return null; // tanınmayanlar için null dön
	}
}

export function getStandingsTeamStatus(description: string) {
	return (
		<span
			className={cnClient(
				'block w-1 h-5.5 bg-transparent mr-1',
				description === 'Champions League' && 'bg-standings-upper!',
				(description === 'Champions League Qualification' ||
					description === 'UEFA Europa League Qualification' ||
					description === 'Conference League Qualification') &&
					'bg-standings-playoff!',
				(description === 'Relegation' || description === 'Relegation Playoff') && 'bg-standings-down!'
			)}
		/>
	);
}

export function getStandingsTeamFormStatus(form: string) {
	const formArray = form.split('');
	return (
		<div className="flex flex-row items-center justify-end gap-1">
			{formArray.map((status, index) => (
				<span
					key={index}
					className={cnClient(
						'w-3 h-3 rounded-full block',
						status === 'W' ? 'bg-standings-upper' : status === 'D' ? 'bg-standings-playoff' : 'bg-standings-down'
					)}
				/>
			))}
		</div>
	);
}

export function getStandingsTeamBttsStatus(btts: string) {
	const bttsArray = btts.split('');
	return (
		<div className="flex flex-row items-center justify-end gap-1.5">
			{bttsArray.map((status, index) => (
				<span
					key={index}
					className={cnClient(
						'w-2.5 h-[2px] block',
						status === 'Y'
							? 'bg-down'
							: "bg-up relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-[2px] before:h-2.5 before:bg-up"
					)}
				/>
			))}
		</div>
	);
}

export function getUnderOverStandingsStatus(form: string) {
	const formArray = form.split('');
	return (
		<div className="flex flex-row items-center justify-end gap-1.5">
			{formArray.map((status, index) => (
				<span
					key={index}
					className={cnClient(
						'w-2.5 h-[2px] block',
						status === 'A'
							? 'bg-down'
							: "bg-up relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-[2px] before:h-2.5 before:bg-up"
					)}
				/>
			))}
		</div>
	);
}

export function groupByFixtureDate(data: any[]) {
	const map: Record<string, { date: string; fixtures: any[] }> = {};

	data.forEach((item) => {
		const date = item.fixture.date.split('T')[0];

		if (!map[date]) {
			map[date] = {
				date,
				fixtures: [],
			};
		}

		map[date].fixtures.push(item);
	});

	return Object.values(map);
}

export function groupByLeagueId(data: any[]) {
	const map: Record<string, { league: { id: number; name: string }; fixtures: any[] }> = {};

	data.forEach((item) => {
		const { id, name } = item.league;

		if (!map[id]) {
			map[id] = {
				league: { id, name },
				fixtures: [],
			};
		}

		map[id].fixtures.push(item);
	});

	return Object.values(map);
}

export function fixtureStatusToText(status: string) {
	const statusTranslations: Record<string, { short: string; long: string }> = {
		TBD: { short: 'TS', long: 'Zaman Belirlenecek' },
		NS: { short: 'BA', long: 'Başlamadı' },
		'1H': { short: 'İY', long: 'İlk Yarı Oynanıyor' },
		HT: { short: 'DA', long: 'Devre Arası' },
		'2H': { short: '2Y', long: 'İkinci Yarı Oynanıyor' },
		ET: { short: 'UZ', long: 'Uzatma Devreleri Oynanıyor' },
		BT: { short: 'MO', long: 'Uzatma Arası / Mola' },
		P: { short: 'P', long: 'Penaltı Atışları Oynanıyor' },
		SUSP: { short: 'AS', long: 'Maç Askıya Alındı' },
		INT: { short: 'KS', long: 'Maç Kısa Süreli Durduruldu' },
		FT: { short: 'MS', long: 'Maç Sona Erdi' },
		AET: { short: 'UO', long: 'Uzatmalar Sonunda Bitti' },
		PEN: { short: 'PS', long: 'Penaltı Atışları Sonunda Bitti' },
		PST: { short: 'ER', long: 'Maç Ertelendi' },
		CANC: { short: 'İPT', long: 'Maç İptal Edildi' },
		ABD: { short: 'TRK', long: 'Maç Terk Edildi' },
		AWD: { short: 'HÜK', long: 'Hükmen Karar' },
		WO: { short: 'RSÇ', long: 'Rakip Sahaya Çıkmadı' },
		LIVE: { short: 'CANLI', long: 'Maç Devam Ediyor' },
	};

	return statusTranslations[status];
}

export function fixtureLiveStatusToText(status: string) {
	switch (status) {
		case '1H':
			return true;
		case 'HT':
			return true;
		case '2H':
			return true;
		case 'ET':
			return true;
		case 'BT':
			return true;
		case 'P':
			return true;
		case 'SUSP':
			return true;
		case 'INT':
			return true;
		case 'LIVE':
			return true;
		default:
			return false;
	}
}
export function fixtureStatus(status: string) {
	switch (status) {
		case 'TBD':
			return 'BA';
		case 'NS':
			return 'BA';

		case '1H':
			return 'CANLI';
		case 'HT':
			return 'CANLI';
		case '2H':
			return 'CANLI';
		case 'ET':
			return 'CANLI';
		case 'BT':
			return 'CANLI';
		case 'P':
			return 'CANLI';
		case 'SUSP':
			return 'CANLI';
		case 'INT':
			return 'CANLI';
		case 'LIVE':
			return 'CANLI';

		case 'FT':
			return 'MS';
		case 'AET':
			return 'MS';
		case 'PEN':
			return 'MS';
		case 'PST':
			return 'MS';
		case 'CANC':
			return 'MS';
		case 'ABD':
			return 'MS';
		case 'AWD':
			return 'MS';
		case 'WO':
			return 'MS';

		default:
			return '';
	}
}
